import { useState, useEffect, useCallback, useRef } from 'react';
import { setTheme as applyAndPersistTheme } from '../../lib/theme';
import { getChatFont, setChatFont, CHAT_FONT_OPTIONS, type ChatFont } from '../../lib/chatFont';
import { APP } from './tokens';
import type { RiskPreferences, ClauseFlagSeverity } from '@corner/shared';
// Lazy-imported inside ContractReviewSettings to avoid circular init in production bundle
// import { getRiskPreferences, updateRiskPreferences } from '../../api/clauseReviewApi';

// ─── Types ──────────────────────────────────────

export interface SettingsUser {
  name: string;
  email: string;
  plan: 'free' | 'pro' | 'enterprise';
  avatarUrl?: string | null;
  signatureId?: string | null;
  initialsId?: string | null;
  phone?: string;
  address?: string;
  company?: string;
  jobTitle?: string;
  updatedAt?: string | null;
  usage?: { aiRequestsThisMonth: number; pagesThisMonth: number };
}

export interface BillingInfo {
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired' | null;
  billingCycle: 'monthly' | 'yearly' | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface SettingsPanelProps {
  user: SettingsUser | null;
  onClose: () => void;
  onSignOut: () => void;
  onUpgrade: () => void;
  onSaveProfile?: (fields: { displayName?: string; phone?: string; address?: string; company?: string; jobTitle?: string }) => void;
  onEditSignature?: () => void;
  onDeleteSignature?: () => void;
  onHelpSupport?: () => void;
  onManageBilling?: () => void;
  onCancelPlan?: () => Promise<void>;
  onReactivatePlan?: () => Promise<void>;
  onIntegrations?: () => void;
  onUploadAvatar?: (file: File) => Promise<string | null>;
  onDeleteAvatar?: () => Promise<void>;
  billingInfo?: BillingInfo | null;
}

const F = APP.fontFamily;

// ─── Section divider ────────────────────────────

function SectionDivider() {
  return <div style={{ height: 1, backgroundColor: APP.border, flexShrink: 0 }} />;
}

// ─── Section header ─────────────────────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{ paddingBlock: 20, paddingInline: 24 }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: APP.textPrimary, lineHeight: '18px' }}>{title}</span>
    </div>
  );
}

// ─── Setting row ────────────────────────────────

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBlock: 14, paddingInline: 24 }}>
      <span style={{ fontSize: 14, fontWeight: 400, color: APP.textDark, lineHeight: '18px' }}>{label}</span>
      {children}
    </div>
  );
}

// ─── Nav row (clickable, with chevron) ──────────

function NavRow({ label, onClick, danger }: { label: string; onClick?: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      className="btn-hover"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBlock: 14,
        paddingInline: 24,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: F,
        fontSize: 14,
        fontWeight: 400,
        color: danger ? '#C0392B' : APP.textDark,
        lineHeight: '18px',
        textAlign: 'left',
        width: '100%',
      }}
    >
      {label}
      {danger ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C0392B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={APP.iconDefault} strokeWidth="2" style={{ flexShrink: 0 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
      )}
    </button>
  );
}

// ─── Theme segmented control ────────────────────

function ThemeSegment({ value, onChange }: { value: 'light' | 'auto' | 'dark'; onChange: (v: 'light' | 'auto' | 'dark') => void }) {
  const options: Array<{ key: 'light' | 'auto' | 'dark'; label: string }> = [
    { key: 'light', label: 'Light' },
    { key: 'auto', label: 'Auto' },
    { key: 'dark', label: 'Dark' },
  ];
  return (
    <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', border: `1px solid ${APP.border}` }}>
      {options.map((o, i) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            style={{
              paddingBlock: 5,
              paddingInline: 14,
              backgroundColor: active ? APP.bgCardBody : APP.bgContent,
              border: 'none',
              borderLeft: i > 0 ? `1px solid ${APP.border}` : 'none',
              cursor: 'pointer',
              fontFamily: F,
              fontSize: 13,
              fontWeight: active ? 500 : 400,
              color: active ? APP.textPrimary : APP.textMuted,
              lineHeight: '16px',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Usage stat ─────────────────────────────────

function UsageStat({ label, current, max, color, unlimited }: { label: string; current: number; max: number; color: string; unlimited?: boolean }) {
  const pct = unlimited ? 100 : Math.min(100, (current / max) * 100);
  return (
    <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%', display: 'flex', flexDirection: 'column', borderRadius: 8, paddingBlock: 14, paddingInline: 14, gap: 8, backgroundColor: APP.bgContent }}>
      <div style={{ fontSize: 12, color: APP.textMuted, lineHeight: '16px', fontFamily: F }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline' }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: APP.textPrimary, lineHeight: '24px', fontFamily: F }}>{current} </span>
        {unlimited ? (
          <span style={{ fontSize: 11, color: APP.textMuted, lineHeight: '14px', fontFamily: F }}>Unlimited</span>
        ) : (
          <span style={{ fontSize: 14, color: APP.textMuted, lineHeight: '18px', fontFamily: F }}>/ {max}</span>
        )}
      </div>
      <div style={{ height: 3, borderRadius: 2, backgroundColor: APP.border, flexShrink: 0 }}>
        <div style={{ height: '100%', borderRadius: 2, backgroundColor: color, width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Plan badge ─────────────────────────────────

function PlanBadge({ plan }: { plan: 'free' | 'pro' | 'enterprise' }) {
  const isPaid = plan === 'pro' || plan === 'enterprise';
  const label = plan === 'enterprise' ? 'ENTERPRISE PLAN' : plan === 'pro' ? 'PRO PLAN' : 'FREE PLAN';
  return (
    <div style={{ display: 'flex', alignItems: 'center', borderRadius: 20, paddingBlock: 4, paddingInline: 10, gap: 5, backgroundColor: isPaid ? '#2B2420' : APP.bgContent }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isPaid ? '#D46A3A' : APP.textMuted, flexShrink: 0 }} />
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', color: isPaid ? '#FFFFFF' : APP.textSecondary, lineHeight: '12px', fontFamily: F }}>
        {label}
      </span>
    </div>
  );
}

// ─── Contract Review Settings ───────────────────

const CLAUSE_TYPE_LABELS: Record<string, string> = {
  non_compete: 'Non-compete / Non-solicit',
  ip_assignment: 'IP Assignment',
  indemnification: 'Indemnification',
  limitation_of_liability: 'Limitation of Liability',
  termination: 'Termination & Exit',
};

const SEVERITY_OPTIONS: { value: ClauseFlagSeverity; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function ContractReviewSettings() {
  const [prefs, setPrefs] = useState<RiskPreferences | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Dynamic import to avoid circular initialization in production bundle
    import('../../api/clauseReviewApi').then(({ getRiskPreferences }) => {
      getRiskPreferences().then(setPrefs).catch(() => {});
    });
  }, []);

  const handleSeverityChange = useCallback((clauseType: string, severity: ClauseFlagSeverity | 'default') => {
    if (!prefs) return;
    const next = { ...prefs.severityOverrides };
    if (severity === 'default') {
      delete next[clauseType];
    } else {
      next[clauseType] = severity;
    }
    const updated = { ...prefs, severityOverrides: next };
    setPrefs(updated);
    setSaving(true);
    import('../../api/clauseReviewApi').then(({ updateRiskPreferences }) => {
      updateRiskPreferences({ severityOverrides: next }).finally(() => setSaving(false));
    });
  }, [prefs]);

  const handleAutoScanToggle = useCallback(() => {
    if (!prefs) return;
    const updated = { ...prefs, autoScanEnabled: !prefs.autoScanEnabled };
    setPrefs(updated);
    setSaving(true);
    import('../../api/clauseReviewApi').then(({ updateRiskPreferences }) => {
      updateRiskPreferences({ autoScanEnabled: updated.autoScanEnabled }).finally(() => setSaving(false));
    });
  }, [prefs]);

  if (!prefs) return null;
  // Ensure severityOverrides exists (API may return prefs without it)
  if (!prefs.severityOverrides) prefs.severityOverrides = {};

  return (
    <>
      <SectionHeader title="Contract Review" />
      <div style={{ paddingInline: 24, paddingBottom: 8 }}>
        <p style={{ margin: 0, fontSize: 11, color: APP.textMuted, lineHeight: '16px', fontFamily: F }}>
          Adjust how clause review flags risk for key clause types.
          {saving && <span style={{ color: '#D97645', marginLeft: 6 }}>Saving...</span>}
        </p>
      </div>

      {/* Severity overrides */}
      {Object.entries(CLAUSE_TYPE_LABELS).map(([type, label]) => (
        <div key={type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBlock: 8, paddingInline: 24 }}>
          <span style={{ fontSize: 13, fontWeight: 400, color: APP.textDark, lineHeight: '16px', fontFamily: F }}>{label}</span>
          <select
            value={prefs.severityOverrides[type] ?? 'default'}
            onChange={(e) => handleSeverityChange(type, e.target.value as ClauseFlagSeverity | 'default')}
            style={{
              fontSize: 12, fontFamily: F, color: APP.textDark,
              padding: '4px 8px', borderRadius: 4,
              border: `1px solid ${APP.border}`, backgroundColor: '#FDFCFB',
              cursor: 'pointer',
            }}
          >
            <option value="default">Default</option>
            {SEVERITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      ))}

      {/* Auto-scan toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBlock: 12, paddingInline: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 400, color: APP.textDark, lineHeight: '16px', fontFamily: F }}>Auto-scan imports</span>
          <span style={{ fontSize: 11, color: APP.textMuted, lineHeight: '14px', fontFamily: F }}>Automatically scan imported documents for risk</span>
        </div>
        <button
          type="button"
          onClick={handleAutoScanToggle}
          style={{
            width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
            backgroundColor: prefs.autoScanEnabled ? '#D97645' : '#D4CFC8',
            position: 'relative', flexShrink: 0, transition: 'background-color 150ms',
          }}
        >
          <div style={{
            width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff',
            position: 'absolute', top: 2,
            left: prefs.autoScanEnabled ? 18 : 2,
            transition: 'left 150ms',
          }} />
        </button>
      </div>
    </>
  );
}

// ─── Main Panel ─────────────────────────────────

export default function SettingsPanel({
  user,
  onClose,
  onSignOut,
  onUpgrade,
  onSaveProfile,
  onEditSignature,
  onDeleteSignature,
  onHelpSupport,
  onManageBilling,
  onCancelPlan,
  onReactivatePlan,
  onIntegrations,
  onUploadAvatar,
  onDeleteAvatar,
  billingInfo,
}: SettingsPanelProps) {
  const [theme, setThemeState] = useState<'light' | 'auto' | 'dark'>(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('corner:theme') : null;
    return (stored === 'light' || stored === 'dark') ? stored : 'auto';
  });
  const setTheme = (value: 'light' | 'auto' | 'dark') => {
    setThemeState(value);
    applyAndPersistTheme(value);
  };
  const [displayName, setDisplayName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [address, setAddress] = useState(user?.address ?? '');
  const [company, setCompany] = useState(user?.company ?? '');
  const [jobTitle, setJobTitle] = useState(user?.jobTitle ?? '');
  const [dirty, setDirty] = useState(false);

  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadAvatar) return;
    setAvatarUploading(true);
    try {
      await onUploadAvatar(file);
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  }, [onUploadAvatar]);

  const isPro = user?.plan === 'pro' || user?.plan === 'enterprise';
  const initial = user?.name?.charAt(0)?.toUpperCase() ?? '?';
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const [chatFont, setChatFontState] = useState<ChatFont>(getChatFont);
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const fontDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fontDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target as Node)) setFontDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [fontDropdownOpen]);

  const handleFontChange = useCallback((font: ChatFont) => {
    setChatFontState(font);
    setChatFont(font);
    setFontDropdownOpen(false);
  }, []);

  useEffect(() => {
    setDisplayName(user?.name ?? '');
    setPhone(user?.phone ?? '');
    setAddress(user?.address ?? '');
    setCompany(user?.company ?? '');
    setJobTitle(user?.jobTitle ?? '');
    setDirty(false);
  }, [user?.name, user?.phone, user?.address, user?.company, user?.jobTitle]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const checkDirty = useCallback((fields: { displayName: string; phone: string; address: string; company: string; jobTitle: string }) => {
    setDirty(
      fields.displayName !== (user?.name ?? '') ||
      fields.phone !== (user?.phone ?? '') ||
      fields.address !== (user?.address ?? '') ||
      fields.company !== (user?.company ?? '') ||
      fields.jobTitle !== (user?.jobTitle ?? '')
    );
  }, [user?.name, user?.phone, user?.address, user?.company, user?.jobTitle]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayName(e.target.value);
    checkDirty({ displayName: e.target.value, phone, address, company, jobTitle });
  }, [phone, address, company, jobTitle, checkDirty]);

  const handleFieldChange = useCallback((field: 'phone' | 'address' | 'company' | 'jobTitle', value: string) => {
    const setters = { phone: setPhone, address: setAddress, company: setCompany, jobTitle: setJobTitle };
    setters[field](value);
    checkDirty({ displayName, phone, address, company, jobTitle, [field]: value });
  }, [displayName, phone, address, company, jobTitle, checkDirty]);

  const handleSave = useCallback(() => {
    if (!dirty) return;
    const fields: Record<string, string> = {};
    if (displayName.trim() !== (user?.name ?? '')) fields.displayName = displayName.trim();
    if (phone !== (user?.phone ?? '')) fields.phone = phone;
    if (address !== (user?.address ?? '')) fields.address = address;
    if (company !== (user?.company ?? '')) fields.company = company;
    if (jobTitle !== (user?.jobTitle ?? '')) fields.jobTitle = jobTitle;
    if (Object.keys(fields).length > 0) onSaveProfile?.(fields);
    setDirty(false);
  }, [dirty, displayName, phone, address, company, jobTitle, user, onSaveProfile]);

  const contactFieldsEmpty = !user?.phone && !user?.address && !user?.company && !user?.jobTitle;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%', fontFamily: F, overflow: 'hidden' }}>
      {/* ── Header bar ── */}
      <div
        style={{
          height: 52,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          paddingInline: 24,
          gap: 12,
          borderBottom: `1px solid ${APP.border}`,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: F, fontSize: 12, fontWeight: 400, color: APP.textMuted, lineHeight: '16px', padding: 0 }}
        >
          ← Back
        </button>
        <span style={{ fontSize: 18, fontWeight: 700, color: APP.textDark, lineHeight: '22px', letterSpacing: '-0.02em' }}>
          Settings
        </span>
      </div>

      {/* ── Scrollable content ── */}
      <div style={{ flexGrow: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 600, marginInline: 'auto' }}>

          {/* ── Profile ── */}
          <SectionHeader title="Profile" />
          <div style={{ paddingInline: 24, paddingBottom: 20 }}>
            {/* Avatar + info row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div
                style={{ width: 56, height: 56, position: 'relative', flexShrink: 0, cursor: onUploadAvatar ? 'pointer' : 'default' }}
                onClick={() => onUploadAvatar && avatarInputRef.current?.click()}
              >
                <div style={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#8B7355', overflow: 'hidden' }}>
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="Avatar" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 20, fontWeight: 700, color: '#FFFFFF' }}>{initial}</span>
                  )}
                </div>
                {onUploadAvatar && (
                  <div style={{ position: 'absolute', bottom: -2, right: -2, width: 22, height: 22, borderRadius: '50%', backgroundColor: APP.bgCardBody, border: `2px solid ${APP.bgContent}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {avatarUploading ? (
                      <div style={{ width: 10, height: 10, border: '2px solid #D97645', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                    ) : (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M7.5 1.5L8.5 2.5L3.5 7.5L1 8L1.5 5.5L6.5 0.5L7.5 1.5Z" stroke={APP.textSecondary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                )}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: APP.textDark, lineHeight: '18px' }}>{user?.name ?? 'Guest'}</span>
                <span style={{ fontSize: 12, color: APP.textMuted, lineHeight: '16px', marginTop: 2 }}>{user?.email ?? ''}</span>
                {user?.avatarUrl && onDeleteAvatar && (
                  <button
                    type="button"
                    onClick={async () => { setAvatarUploading(true); try { await onDeleteAvatar(); } finally { setAvatarUploading(false); } }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 4, fontSize: 11, fontWeight: 400, color: APP.textMuted, fontFamily: F, textAlign: 'left' }}
                  >
                    Remove photo
                  </button>
                )}
              </div>
              <PlanBadge plan={user?.plan ?? 'free'} />
            </div>

            {/* Display name */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: APP.textCardDetail, lineHeight: '16px' }}>Display name</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={displayName}
                  onChange={handleNameChange}
                  style={{
                    flexGrow: 1,
                    height: 36,
                    borderRadius: 7,
                    paddingInline: 10,
                    backgroundColor: APP.bgCardBody,
                    border: `1px solid ${dirty ? '#D97645' : APP.border}`,
                    fontFamily: F,
                    fontSize: 14,
                    color: APP.textPrimary,
                    lineHeight: '18px',
                    outline: 'none',
                  }}
                />
                {dirty && (
                  <button
                    type="button"
                    onClick={handleSave}
                    style={{
                      height: 36,
                      borderRadius: 7,
                      paddingInline: 16,
                      border: 'none',
                      backgroundColor: '#D97645',
                      cursor: 'pointer',
                      fontFamily: F,
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#FFFFFF',
                      lineHeight: '16px',
                      flexShrink: 0,
                    }}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>

            {/* Email (read-only) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: APP.textCardDetail, lineHeight: '16px' }}>Email</label>
              <div
                style={{
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 7,
                  paddingInline: 10,
                  backgroundColor: APP.bgContent,
                  border: `1px solid ${APP.border}`,
                }}
              >
                <span style={{ fontSize: 14, color: APP.textMuted, lineHeight: '18px' }}>{user?.email ?? ''}</span>
              </div>
              <span style={{ fontSize: 11, color: APP.textMuted, lineHeight: '14px' }}>Cannot be changed after registration</span>
            </div>

            {/* ── Contact information ── */}
            <div style={{ marginTop: 20 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: APP.textSecondary, lineHeight: '16px', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>Contact information</span>

              {/* Nudge chip */}
              {contactFieldsEmpty && (
                <div
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 12px',
                    borderRadius: 8,
                    backgroundColor: APP.bgQuickAction,
                    border: `1px solid ${APP.border}`,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="7" cy="7" r="6" stroke={APP.textMuted} strokeWidth="1.2" fill="none" />
                    <path d="M7 4v3" stroke={APP.textMuted} strokeWidth="1.3" strokeLinecap="round" />
                    <circle cx="7" cy="9.5" r="0.7" fill={APP.textMuted} />
                  </svg>
                  <span style={{ fontSize: 12, color: APP.textMuted, lineHeight: '16px' }}>Add your contact info for faster document signing</span>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
                {/* Phone */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, color: APP.textCardDetail, lineHeight: '16px' }}>Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    style={{
                      height: 36, borderRadius: 7, paddingInline: 10,
                      backgroundColor: APP.bgCardBody,
                      border: `1px solid ${phone !== (user?.phone ?? '') ? '#D97645' : APP.border}`,
                      fontFamily: F, fontSize: 14, color: APP.textPrimary, lineHeight: '18px', outline: 'none',
                    }}
                  />
                </div>

                {/* Address */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, color: APP.textCardDetail, lineHeight: '16px' }}>Address</label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                    placeholder="123 Main St, City, State 12345"
                    style={{
                      height: 36, borderRadius: 7, paddingInline: 10,
                      backgroundColor: APP.bgCardBody,
                      border: `1px solid ${address !== (user?.address ?? '') ? '#D97645' : APP.border}`,
                      fontFamily: F, fontSize: 14, color: APP.textPrimary, lineHeight: '18px', outline: 'none',
                    }}
                  />
                </div>

                {/* Company + Job title — side by side */}
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: APP.textCardDetail, lineHeight: '16px' }}>Company</label>
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => handleFieldChange('company', e.target.value)}
                      placeholder="Acme Inc."
                      style={{
                        height: 36, borderRadius: 7, paddingInline: 10,
                        backgroundColor: APP.bgCardBody,
                        border: `1px solid ${company !== (user?.company ?? '') ? '#D97645' : APP.border}`,
                        fontFamily: F, fontSize: 14, color: APP.textPrimary, lineHeight: '18px', outline: 'none',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: APP.textCardDetail, lineHeight: '16px' }}>Job title</label>
                    <input
                      type="text"
                      value={jobTitle}
                      onChange={(e) => handleFieldChange('jobTitle', e.target.value)}
                      placeholder="Real Estate Agent"
                      style={{
                        height: 36, borderRadius: 7, paddingInline: 10,
                        backgroundColor: APP.bgCardBody,
                        border: `1px solid ${jobTitle !== (user?.jobTitle ?? '') ? '#D97645' : APP.border}`,
                        fontFamily: F, fontSize: 14, color: APP.textPrimary, lineHeight: '18px', outline: 'none',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Save button for contact fields */}
              {dirty && (
                <button
                  type="button"
                  onClick={handleSave}
                  style={{
                    marginTop: 14, height: 36, borderRadius: 7, paddingInline: 16,
                    border: 'none', backgroundColor: '#D97645', cursor: 'pointer',
                    fontFamily: F, fontSize: 13, fontWeight: 500, color: '#FFFFFF', lineHeight: '16px',
                  }}
                >
                  Save
                </button>
              )}
            </div>
          </div>

          <SectionDivider />

          {/* ── Signature ── */}
          <SectionHeader title="Signature" />
          <div style={{ paddingInline: 24, paddingBottom: 20 }}>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: APP.textMuted, lineHeight: '16px' }}>Used for e-signing documents</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  flexGrow: 1,
                  height: 72,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  backgroundColor: APP.bgContent,
                  border: `1px solid ${APP.border}`,
                }}
              >
                {user?.signatureId ? (
                  <img
                    key={`${user.signatureId}:${user.updatedAt ?? ''}`}
                    src={`/api/auth/me/signature-preview?type=signature&t=${encodeURIComponent(user.updatedAt ?? user.signatureId)}`}
                    alt="Signature"
                    style={{ maxHeight: 52, maxWidth: '85%', objectFit: 'contain' }}
                  />
                ) : (
                  <svg width="120" height="36" viewBox="0 0 120 36" fill="none" style={{ opacity: 0.3 }}>
                    <path d="M10 28c10-20 20 10 30-5s20 15 30-5 20 20 30-5" stroke={APP.iconDefault} strokeWidth="2" strokeLinecap="round" fill="none" />
                  </svg>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn-hover"
                  onClick={onEditSignature}
                  style={{ borderRadius: 6, paddingBlock: 6, paddingInline: 14, backgroundColor: 'transparent', border: `1px solid ${APP.border}`, cursor: 'pointer', fontFamily: F, fontSize: 12, color: APP.textPrimary, lineHeight: '16px' }}
                >
                  {user?.signatureId ? 'Edit' : 'Add signature'}
                </button>
                {user?.signatureId && (
                  <button
                    type="button"
                    className="btn-hover"
                    onClick={onDeleteSignature}
                    style={{ borderRadius: 6, paddingBlock: 6, paddingInline: 14, backgroundColor: 'transparent', border: `1px solid ${APP.border}`, cursor: 'pointer', fontFamily: F, fontSize: 12, color: '#C0392B', lineHeight: '16px' }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>

          <SectionDivider />

          {/* ── Appearance ── */}
          <SectionHeader title="Appearance" />
          <SettingRow label="Theme">
            <ThemeSegment value={theme} onChange={setTheme} />
          </SettingRow>
          <SettingRow label="Chat font">
            <div ref={fontDropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setFontDropdownOpen((prev) => !prev)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 7,
                  paddingBlock: 5,
                  paddingInline: 14,
                  gap: 6,
                  backgroundColor: APP.bgCardBody,
                  border: `1px solid ${APP.border}`,
                  fontSize: 13,
                  color: APP.textDark,
                  lineHeight: '16px',
                  cursor: 'pointer',
                  fontFamily: F,
                }}
              >
                {CHAT_FONT_OPTIONS.find((o) => o.key === chatFont)?.label ?? 'Geist'}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, transform: fontDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}>
                  <path d="M2.5 3.5L5 6l2.5-2.5" stroke={APP.iconDefault} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {fontDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    minWidth: 200,
                    backgroundColor: APP.bgContextMenu,
                    border: `1px solid ${APP.borderContextMenu}`,
                    borderRadius: 8,
                    boxShadow: APP.shadowContextMenu,
                    zIndex: 50,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 4,
                  }}
                >
                  {CHAT_FONT_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className="btn-hover"
                      onClick={() => handleFontChange(option.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '8px 12px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderRadius: 6,
                        fontFamily: option.preview,
                        fontSize: 13,
                        fontWeight: chatFont === option.key ? 500 : 400,
                        color: APP.textPrimary,
                        lineHeight: '16px',
                        textAlign: 'left',
                      }}
                    >
                      {option.label}
                      {chatFont === option.key && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M3 7.5l3 3 5-6" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </SettingRow>
          <div style={{ height: 8 }} />

          <SectionDivider />

          {/* ── Contract Review Preferences ── */}
          <ContractReviewSettings />

          <SectionDivider />

          {/* ── Keyboard Shortcuts ── */}
          <SectionHeader title="Keyboard Shortcuts" />
          <div style={{ paddingInline: 24, paddingBottom: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { key: '@', action: 'Insert workflow in chat' },
                { key: 'C', action: 'Toggle comment mode' },
                { key: 'Esc', action: 'Close panel / exit mode' },
                { key: 'Enter', action: 'Send message' },
              ].map(({ key, action }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: APP.textSecondary, fontFamily: F }}>{action}</span>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: 'monospace',
                    color: APP.textDark,
                    backgroundColor: APP.bgCardBody,
                    border: `1px solid ${APP.border}`,
                    borderRadius: 4,
                    paddingBlock: 2,
                    paddingInline: 6,
                    lineHeight: '16px',
                  }}>{key}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ height: 8 }} />

          <SectionDivider />

          {/* ── Account ── */}
          <SectionHeader title="Account" />
          <NavRow label="Integrations" onClick={onIntegrations} />
          <NavRow label="Help & support" onClick={onHelpSupport} />
          <NavRow label="Sign out" onClick={onSignOut} danger />
          <div style={{ height: 8 }} />

          <SectionDivider />

          {/* ── Plan & Billing ── */}
          <SectionHeader title="Plan & Billing" />
          <div style={{ paddingInline: 24, paddingBottom: 24 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <UsageStat label="AI requests this month" current={user?.usage?.aiRequestsThisMonth ?? 0} max={isPro ? 1 : 20} color={isPro ? '#5A9B7A' : '#D97645'} unlimited={isPro} />
              <UsageStat label="Pages processed" current={user?.usage?.pagesThisMonth ?? 0} max={isPro ? 1 : 50} color={isPro ? '#5A9B7A' : '#D97645'} unlimited={isPro} />
            </div>

            {isPro ? (
              /* Pro/Enterprise: subscription info */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Trial banner */}
                {billingInfo?.status === 'trialing' && billingInfo.trialEnd && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderRadius: 8, paddingBlock: 12, paddingInline: 14, backgroundColor: '#FFF7ED', border: '1px solid #FDBA74' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span style={{ fontSize: 12, color: '#9A3412', lineHeight: '16px', fontFamily: F }}>
                      Free trial · {Math.max(0, Math.ceil((new Date(billingInfo.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} days remaining
                    </span>
                  </div>
                )}

                {/* Plan details */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 8, paddingBlock: 14, paddingInline: 14, border: `1px solid ${APP.border}` }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: APP.textDark, lineHeight: '16px' }}>
                      {billingInfo?.billingCycle === 'yearly' ? 'Yearly plan · $99/year' : 'Monthly plan · $12.99/month'}
                    </span>
                    <span style={{ fontSize: 11, color: APP.textMuted, lineHeight: '14px', marginTop: 2 }}>
                      {billingInfo?.status === 'trialing'
                        ? `First charge on ${billingInfo.trialEnd ? new Date(billingInfo.trialEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}`
                        : billingInfo?.cancelAtPeriodEnd
                          ? `Access until ${billingInfo.currentPeriodEnd ? new Date(billingInfo.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}`
                          : `Renews ${billingInfo?.currentPeriodEnd ? new Date(billingInfo.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}`
                      }
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn-hover"
                    onClick={onManageBilling}
                    style={{ borderRadius: 7, paddingBlock: 7, paddingInline: 16, backgroundColor: APP.bgCardBody, border: `1px solid ${APP.border}`, cursor: 'pointer', fontFamily: F, fontSize: 12, color: APP.textSecondary, lineHeight: '16px', flexShrink: 0 }}
                  >
                    Manage billing
                  </button>
                </div>

                {/* Cancel / Reactivate */}
                {billingInfo?.cancelAtPeriodEnd ? (
                  <button
                    type="button"
                    className="btn-hover"
                    onClick={onReactivatePlan}
                    style={{ width: '100%', borderRadius: 7, paddingBlock: 9, border: `1px solid ${APP.border}`, backgroundColor: 'transparent', cursor: 'pointer', fontFamily: F, fontSize: 12, fontWeight: 500, color: '#D97645', lineHeight: '16px', textAlign: 'center' }}
                  >
                    Reactivate subscription
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-hover"
                    onClick={onCancelPlan}
                    style={{ width: '100%', borderRadius: 7, paddingBlock: 9, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontFamily: F, fontSize: 11, color: APP.textMuted, lineHeight: '14px', textAlign: 'center' }}
                  >
                    Cancel subscription
                  </button>
                )}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <button
                    type="button"
                    onClick={() => setSelectedPlan('monthly')}
                    style={{ flexGrow: 1, flexBasis: '0%', display: 'flex', flexDirection: 'column', borderRadius: 10, paddingBlock: 18, paddingInline: 18, gap: 8, border: selectedPlan === 'monthly' ? '1.5px solid #D46A3A' : `1px solid ${APP.border}`, backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: F, transition: 'border-color 150ms ease' }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, color: APP.textPrimary, lineHeight: '18px' }}>Monthly</span>
                    <div style={{ display: 'flex', alignItems: 'baseline' }}>
                      <span style={{ fontSize: 24, fontWeight: 700, color: APP.textPrimary, lineHeight: '30px' }}>$12.99</span>
                      <span style={{ fontSize: 13, color: APP.textMuted, lineHeight: '16px' }}>/month</span>
                    </div>
                    <span style={{ fontSize: 12, color: APP.textMuted, lineHeight: '16px' }}>Unlimited AI · Unlimited operations</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedPlan('yearly')}
                    style={{ flexGrow: 1, flexBasis: '0%', display: 'flex', flexDirection: 'column', position: 'relative', borderRadius: 10, paddingBlock: 18, paddingInline: 18, gap: 8, border: selectedPlan === 'yearly' ? '1.5px solid #D46A3A' : `1px solid ${APP.border}`, backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: F, transition: 'border-color 150ms ease' }}
                  >
                    <div style={{ position: 'absolute', top: -10, right: 12, borderRadius: 20, paddingBlock: 3, paddingInline: 10, backgroundColor: '#D97645' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: '#FFFFFF', lineHeight: '14px' }}>BEST VALUE</span>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: APP.textPrimary, lineHeight: '18px' }}>Yearly</span>
                    <div style={{ display: 'flex', alignItems: 'baseline' }}>
                      <span style={{ fontSize: 24, fontWeight: 700, color: APP.textPrimary, lineHeight: '30px' }}>$99</span>
                      <span style={{ fontSize: 13, color: APP.textMuted, lineHeight: '16px' }}>/year</span>
                    </div>
                    <span style={{ fontSize: 12, color: APP.textMuted, lineHeight: '16px' }}>Save 36% vs monthly</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={onUpgrade}
                  style={{ width: '100%', borderRadius: 7, paddingBlock: 10, border: 'none', backgroundColor: '#D97645', cursor: 'pointer', fontFamily: F, fontSize: 14, fontWeight: 500, color: '#FFFFFF', lineHeight: '18px', textAlign: 'center' }}
                >
                  {selectedPlan === 'yearly' ? 'Upgrade to $99/year' : 'Upgrade to $12.99/month'}
                </button>
              </>
            )}
          </div>

          {/* Bottom spacer */}
          <div style={{ height: 40 }} />
        </div>
      </div>
      {avatarUploading && <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>}
    </div>
  );
}
